// The repository catalogue as the app sees it: every `*.json` under
// `src/data/catalogue/**` loaded through ONE glob + validation path.
//
// This is the read side of the repository store. Documents are bundled (not
// fetched) because they are part of the build — the working tree at build time
// IS the catalogue. Validation happens here rather than at each use: a
// malformed document must be a reported drop, never a document that
// half-loads and then misbehaves somewhere far away.

import { validateDocument, type StoredDocument } from './documents';
import { kindOfDirectory } from './repositoryPaths';

const MODULES = import.meta.glob<{ default: unknown }>('../data/catalogue/*/*.json', {
  eager: true,
});

let cached: StoredDocument[] | null = null;

/** Every valid bundled catalogue document, sorted by id. */
export function bundledDocuments(): StoredDocument[] {
  if (cached) return cached;
  const kept: StoredDocument[] = [];
  for (const [path, module] of Object.entries(MODULES)) {
    const doc = module?.default;
    const issues = validateDocument(doc);
    if (issues.length) {
      warn(path, issues.map((i) => `${i.path || '(root)'} ${i.message}`).join('; '));
      continue;
    }
    const stored = doc as StoredDocument;
    const segments = path.split('/');
    // The filename IS the id — the writer derives one from the other, so a
    // hand-renamed file would leave every reference to it dangling.
    const expected = segments[segments.length - 1].replace(/\.json$/, '');
    if (stored.id !== expected) {
      warn(path, `id "${stored.id}" does not match its filename`);
      continue;
    }
    // …and the directory declares the kind, so a document filed in the wrong
    // collection is a reported mistake rather than a listing that quietly
    // disagrees with the writer's allow-list.
    const dirKind = kindOfDirectory(segments[segments.length - 2] ?? '');
    if (dirKind !== stored.kind) {
      warn(path, `a ${stored.kind} document is filed under ${segments[segments.length - 2]}`);
      continue;
    }
    kept.push(stored);
  }
  kept.sort((a, b) => a.id.localeCompare(b.id));
  cached = kept;
  return kept;
}

function warn(path: string, reason: string): void {
  console.warn(`[catalogue] ignoring ${path}: ${reason}`);
}

/** Test seam: forget the memoised catalogue. */
export function resetBundledDocuments(): void {
  cached = null;
}
